import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import type {
  GridCellParams,
  GridRenderCellParams,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridRowId
} from '@mui/x-data-grid-pro';
import { ClosedOnEmptyCell } from './ClosedOnEmptyCell';
import { cellKey, ExpandableCellContext, isSameCell } from './expandableCellContext';
import type { ExpandedCell } from './expandableCellContext';

export { ExpandableCellProvider } from './expandableCellContext';
export type { ExpandedCell } from './expandableCellContext';

// Both widths are integers, so an exactly-fitting line can report one pixel more than its box.
const TRUNCATION_TOLERANCE_PX = 1;

const isOverflowing = (element: HTMLElement | null): boolean =>
  !!element && element.scrollWidth > element.clientWidth + TRUNCATION_TOLERANCE_PX;

// The click ending a drag-select still fires, and collapsing would take back the text just selected.
const isSelectingText = (): boolean => (window.getSelection()?.toString() ?? '') !== '';

// Give the returned context to an ExpandableCellProvider wrapping the grid, or cells cannot open.
export const useExpandableCells = (visibleFields?: string[], baseRowHeight = 0) => {
  const [expanded, setExpanded] = useState<ExpandedCell | null>(null);
  // Only the cell can answer whether its own value is cut off, so it tells the grid rather than the reverse.
  const truncatedCells = useRef(new Set<string>());

  const reportTruncation = useCallback((rowId: GridRowId, field: string, truncated: boolean) => {
    // Never closes the open cell: virtualization unmounts an unfocused cell each time its row scrolls by.
    if (truncated) truncatedCells.current.add(cellKey(rowId, field));
    else truncatedCells.current.delete(cellKey(rowId, field));
  }, []);

  const toggleCell = useCallback(({ id, field }: GridCellParams) => {
    setExpanded((current) => {
      // Closing first: virtualization drops an unmounted cell's report, so an open one may not be listed.
      if (isSameCell(current, id, field)) return null;

      return truncatedCells.current.has(cellKey(id, field)) ? { rowId: id, field, height: null } : null;
    });
  }, []);

  // Guarded here rather than in toggleCell: a stale selection must not swallow the keyboard toggle.
  const onCellClick = useCallback(
    (params: GridCellParams) => {
      if (isSelectingText()) return;

      toggleCell(params);
    },
    [toggleCell]
  );

  // Enter, because Space pages the grid down and Shift+Space selects the row.
  // An editable cell is left alone: MUI starts editing on the same key, and both would fire at once.
  const onCellKeyDown = useCallback(
    (params: GridCellParams, event: { key: string }) => {
      if (event.key === 'Enter' && !params.isEditable) toggleCell(params);
    },
    [toggleCell]
  );

  // A changed NUMBER re-lays the rows out; 'auto' only re-flags a row MUI already believes is 40px.
  const reportExpandedHeight = useCallback(
    (height: number) => {
      // A widened column can fit the open value on one line, and that measures SHORTER than the grid default.
      // Back to null rather than an early return, or the row stays as tall as the narrower column once needed.
      const next = height > baseRowHeight ? height : null;

      setExpanded((current) => (current && current.height !== next ? { ...current, height: next } : current));
    },
    [baseRowHeight]
  );

  // Hidden, or dropped from the column set by an account's disabled fields — either way its row must not stay tall.
  const active = expanded && (!visibleFields || visibleFields.includes(expanded.field)) ? expanded : null;

  // Forgotten, not just masked: the cell is as gone as an emptied one, so re-showing the column opens nothing.
  useEffect(() => {
    if (expanded && !active) setExpanded(null);
  }, [active, expanded]);

  const getRowHeight = useCallback(
    ({ id }: GridRowHeightParams): GridRowHeightReturnValue => (active && id === active.rowId ? active.height : null),
    [active]
  );

  const clearExpanded = useCallback(() => setExpanded(null), []);

  const context = useMemo(
    () => ({ expanded: active, reportTruncation, reportExpandedHeight, clearExpanded }),
    [active, clearExpanded, reportExpandedHeight, reportTruncation]
  );

  return { context, getRowHeight, onCellClick, onCellKeyDown, clearExpanded };
};

interface ExpandableTextCellProps {
  rowId: GridRowId;
  field: string;
  text: string;
}

export const ExpandableTextCell = ({ rowId, field, text }: ExpandableTextCellProps) => {
  const { expanded, reportTruncation, reportExpandedHeight } = useContext(ExpandableCellContext);
  const textRef = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);

  const isExpanded = isSameCell(expanded, rowId, field);
  const canExpand = !!reportTruncation;
  const isToggleable = canExpand && (truncated || isExpanded);

  const measure = useCallback(() => {
    const overflowing = isOverflowing(textRef.current);
    setTruncated(overflowing);
    reportTruncation?.(rowId, field, overflowing);
  }, [field, reportTruncation, rowId]);

  // Skipped while open, since a wrapped value always measures as fitting; width and value re-ask it.
  useEffect(() => {
    if (isExpanded) return;

    measure();
    const node = textRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => measure());
    observer.observe(node);

    return () => observer.disconnect();
  }, [isExpanded, measure, text]);

  // Measured from the clipped div: it wraps as soon as it is open, whatever height the row still is.
  useLayoutEffect(() => {
    const node = textRef.current;
    if (!isExpanded || !reportExpandedHeight || !node) return;

    // MUI keeps the focused cell rendered once its column scrolls out, at zero width. It still answers
    // scrollHeight, but with nothing to wrap against it measures one word per line.
    const report = () => {
      if (node.isConnected && node.clientWidth > 0) reportExpandedHeight(node.scrollHeight);
    };
    report();
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(report);
    observer.observe(node);

    return () => observer.disconnect();
  }, [isExpanded, reportExpandedHeight, text]);

  // Virtualization unmounts a cell on scroll, and a cell that is gone can no longer be opened.
  useEffect(() => () => reportTruncation?.(rowId, field, false), [field, reportTruncation, rowId]);

  return (
    <Tooltip
      title={truncated && !isExpanded ? text : ''}
      placement="top-start"
      slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}>
      <div
        ref={textRef}
        data-testid="expandable-text-cell"
        data-expanded={isExpanded || undefined}
        // The grid cell owns focus and the Enter handler, so this carries the state a screen reader needs, not a tab stop.
        role={isToggleable ? 'button' : undefined}
        aria-expanded={isToggleable ? isExpanded : undefined}
        style={{
          width: '100%',
          // pre-wrap, not normal: these values carry line breaks the reader opened the cell to read.
          whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          overflowWrap: 'break-word',
          lineHeight: isExpanded ? 1.4 : undefined,
          paddingBlock: isExpanded ? '8px' : undefined,
          cursor: isToggleable ? 'pointer' : 'inherit'
        }}>
        {text}
      </div>
    </Tooltip>
  );
};

// Drop on any long-free-text column; without the grid context it degrades to a hover tooltip.
export const renderExpandableTextCell = (params: GridRenderCellParams) => {
  // The value MUI would have painted, minus the native title tooltip the Tooltip above replaces.
  const rendered = params.formattedValue ?? params.value;
  const text = rendered == null ? '' : String(rendered);
  if (!text)
    return (
      <ClosedOnEmptyCell
        rowId={params.id}
        field={params.field}
      />
    );

  return (
    <ExpandableTextCell
      rowId={params.id}
      field={params.field}
      text={text}
    />
  );
};
